/* =====================================================================
 * DM Conversation Store — 팀 DM 대화 전역 상태 (Zustand)
 * ===================================================================== */

import { create } from 'zustand';
import type { Conversation, UserMessage, ReactionEmoji, Reaction } from '@auw/workspace-utils/types/dm';
import { hasAuthSessionContext } from '@auw/api-client';
import { useUserStore } from './userStore';
import { useChatStore } from './chatStore';
import { isUnauthorizedHttpError } from '@auw/workspace-utils';
import i18n from '@auw/i18n';
import {
  listConversations,
  getConversationMessages,
  getConversation,
  createDirectConversation,
  createGroupConversation,
  markConversationRead as apiMarkRead,
  leaveConversation as apiLeaveConversation,
  toggleMessageReaction as apiToggleReaction,
} from '@auw/api-client';

interface DMConversationState {
  /* ---- 상태 ---- */
  conversations: Conversation[];
  activeConversation: string | null; // conversation_id
  messages: Record<string, UserMessage[]>; // conv_id → messages
  typingUsers: Record<string, Set<string>>; // conv_id → typing user_ids
  isLoaded: boolean;

  /* ---- 액션 ---- */
  loadConversations: (options?: { force?: boolean }) => Promise<void>;
  loadMessages: (convId: string, before?: string) => Promise<boolean>;
  setActiveConversation: (convId: string | null) => void;
  ensureConversation: (convId: string) => Promise<Conversation | null>;

  /** 대화 생성 (1:1) — find-or-create, conversation_id 반환 */
  openDirect: (teamId: string, targetUserId: string) => Promise<string>;
  /** 그룹 대화 생성 */
  openGroup: (teamId: string, name: string, memberIds: string[]) => Promise<string>;

  /** WebSocket으로 수신한 메시지 추가 */
  addMessage: (convId: string, msg: UserMessage) => void;
  /** 대화 목록에서 메타 갱신 (last_message 등) */
  updateConversationMeta: (convId: string, patch: Partial<Conversation>) => void;

  /** 타이핑 상태 */
  setTyping: (convId: string, userId: string, typing: boolean) => void;

  /** 리액션 토글 (낙관적 + 서버 전파) */
  toggleReaction: (convId: string, messageId: string, emoji: ReactionEmoji, userId: string) => void;
  /** 서버 user_reaction 이벤트로 받은 권위적 리액션 상태를 메시지에 반영 */
  setMessageReactions: (convId: string, messageId: string, reactions: Reaction[]) => void;

  /** 읽음 처리 */
  markRead: (convId: string) => Promise<void>;
  /** 탭이 다시 visible이 될 때, hidden 동안 수신된 메시지의 대화를 일괄 읽음 처리 */
  flushPendingReads: () => void;
  /** 대화방 나가기 */
  leaveConversation: (convId: string) => Promise<boolean>;
  /** 내 프로필(이름/아바타) 변경 시 대화 멤버 정보 즉시 반영 */
  syncCurrentUserProfile: (payload: {
    userId: string;
    name?: string | null;
    email?: string | null;
    picture?: string | null;
  }) => void;

  /** 초기화 */
  clear: () => void;
}

/**
 * loadConversations 요청 카운터 — stale 응답이 최신 상태를 덮어쓰는
 * 레이스 컨디션을 방지합니다. WebSocket 메시지가 도착한 직후
 * 로컬 상태가 갱신되면, 그 이전에 시작된 폴링 응답은 무시됩니다.
 */
let _loadConvSeq = 0;
const LOAD_CONVERSATIONS_MIN_INTERVAL_MS = 3_000;
let _loadConversationsInFlight: Promise<void> | null = null;
let _loadConversationsInFlightSeq = 0;
let _lastLoadConversationsAt = 0;

const _pendingReadConvIds = new Set<string>();

function sortConversationsByRecent(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort(
    (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime(),
  );
}

function upsertConversationList(
  conversations: Conversation[],
  conversation: Conversation,
): Conversation[] {
  const existingIndex = conversations.findIndex(
    (item) => item.conversation_id === conversation.conversation_id,
  );

  if (existingIndex === -1) {
    return sortConversationsByRecent([conversation, ...conversations]);
  }

  const next = [...conversations];
  next[existingIndex] = {
    ...next[existingIndex],
    ...conversation,
  };
  return sortConversationsByRecent(next);
}

function getMessageAttachmentFileIds(msg: Pick<UserMessage, 'metadata'>): string[] {
  const raw = msg.metadata?.attachment_files;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const fileId = (entry as { file_id?: unknown }).file_id;
      return typeof fileId === 'string' && fileId.trim() ? fileId.trim() : null;
    })
    .filter((value): value is string => !!value);
}

function buildMessagePreview(msg: Pick<UserMessage, 'content' | 'metadata'>): string {
  const trimmed = msg.content.trim();
  if (trimmed) return trimmed.slice(0, 100);
  const attachmentFileIds = getMessageAttachmentFileIds(msg);
  if (attachmentFileIds.length === 0) return '';
  const raw = msg.metadata?.attachment_files;
  const firstName = Array.isArray(raw)
    ? raw.find((entry) => entry && typeof entry === 'object' && typeof (entry as { name?: unknown }).name === 'string')
    : null;
  const resolvedName = firstName && typeof (firstName as { name?: unknown }).name === 'string'
    ? ((firstName as { name: string }).name.trim() || i18n.t('common:storage.file'))
    : i18n.t('common:storage.file');
  return attachmentFileIds.length === 1 ? resolvedName : i18n.t('common:storage.files', { count: attachmentFileIds.length });
}

function hasSameAttachmentSignature(a: Pick<UserMessage, 'metadata'>, b: Pick<UserMessage, 'metadata'>): boolean {
  const aIds = getMessageAttachmentFileIds(a);
  const bIds = getMessageAttachmentFileIds(b);
  if (aIds.length !== bIds.length) return false;
  return aIds.every((id, idx) => id === bIds[idx]);
}

export const useDMConversationStore = create<DMConversationState>((set, get) => ({
  conversations: [],
  activeConversation: null,
  messages: {},
  typingUsers: {},
  isLoaded: false,

  /* ---- 대화 목록 로드 ---- */
  loadConversations: async (options) => {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && _loadConversationsInFlight) return _loadConversationsInFlight;
    if (!force && now - _lastLoadConversationsAt < LOAD_CONVERSATIONS_MIN_INTERVAL_MS) return Promise.resolve();

    const seq = ++_loadConvSeq;
    const request = (async () => {
      try {
        const { conversations } = await listConversations();
        if (seq !== _loadConvSeq) return; // stale 응답 무시
        set({ conversations: sortConversationsByRecent(conversations), isLoaded: true });
        _lastLoadConversationsAt = Date.now();
      } catch (err) {
        if (isUnauthorizedHttpError(err) && !hasAuthSessionContext()) return;
        console.error('[dmConversationStore] loadConversations failed:', err);
      }
    })();

    _loadConversationsInFlightSeq = seq;
    _loadConversationsInFlight = request.finally(() => {
      if (_loadConversationsInFlightSeq === seq) {
        _loadConversationsInFlight = null;
      }
    });

    return _loadConversationsInFlight;
  },

  /* ---- 메시지 로드 (페이지네이션) ---- */
  loadMessages: async (convId, before) => {
    try {
      const { messages: newMsgs, has_more } = await getConversationMessages(convId, 50, before);
      set((s) => {
        if (before) {
          const existing = s.messages[convId] || [];
          return {
            messages: { ...s.messages, [convId]: [...newMsgs, ...existing] },
          };
        }

        const wsOnly = (s.messages[convId] || []).filter(
          (m) => !newMsgs.some((rm) => rm.message_id === m.message_id),
        );

        const merged = [...newMsgs, ...wsOnly].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );

        return { messages: { ...s.messages, [convId]: merged } };
      });
      return has_more;
    } catch (err) {
      console.error('[dmConversationStore] loadMessages failed:', err);
      return false;
    }
  },

  /* ---- 활성 대화 선택 ---- */
  setActiveConversation: async (convId) => {
    set({ activeConversation: convId });
    // 대화 상세(멤버 포함) 로드
    if (convId) {
      try {
        const detail = await getConversation(convId);
        set((s) => ({
          conversations: s.conversations.map((c) =>
            c.conversation_id === convId ? { ...c, members: detail.members } : c
          ),
        }));
      } catch (err) {
        console.error('[dmConversationStore] getConversation failed:', err);
      }
    }
  },

  ensureConversation: async (convId) => {
    if (!convId) return null;

    const existing = get().conversations.find((c) => c.conversation_id === convId) ?? null;
    if (existing) return existing;

    // 실시간 새 대화 bootstrap이 시작되면, 이전 목록 응답은 stale로 간주한다.
    _loadConvSeq += 1;

    try {
      const detail = await getConversation(convId);
      set((s) => ({
        conversations: upsertConversationList(s.conversations, detail),
        isLoaded: true,
      }));
      return detail;
    } catch (err) {
      console.error('[dmConversationStore] ensureConversation failed:', err);
      await get().loadConversations({ force: true });
      return get().conversations.find((c) => c.conversation_id === convId) ?? null;
    }
  },

  /* ---- 1:1 대화 열기 ---- */
  openDirect: async (teamId, targetUserId) => {
    const conv = await createDirectConversation(teamId, targetUserId);
    set((s) => {
      return {
        conversations: upsertConversationList(s.conversations, conv),
        activeConversation: conv.conversation_id,
      };
    });
    return conv.conversation_id;
  },

  /* ---- 그룹 대화 생성 ---- */
  openGroup: async (teamId, name, memberIds) => {
    const conv = await createGroupConversation(teamId, name, memberIds);
    set((s) => ({
      conversations: [conv, ...s.conversations],
      activeConversation: conv.conversation_id,
    }));
    return conv.conversation_id;
  },

  /* ---- 메시지 추가 (WebSocket) ---- */
  addMessage: (convId, msg) => {
    // 로컬 실시간 이벤트가 발생했으므로, 이전 폴링 응답은 stale로 간주
    _loadConvSeq += 1;

    const isActive = get().activeConversation === convId;
    const isDMModeActive = useChatStore.getState().activeMode === 'message';
    const currentUserId = useUserStore.getState().user?.userId || useUserStore.getState().user?.email || '';
    const isMine = !!currentUserId && msg.sender_id === currentUserId;
    const isIncoming = !isMine;
    const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
    const canAutoRead = isDMModeActive && isActive && isVisible;

    set((s) => {
      const existing = s.messages[convId] || [];
      // 중복 방지
      if (existing.some((m) => m.message_id === msg.message_id)) return s;

      // 낙관적 업데이트(temp_) 메시지를 서버 메시지로 교체
      const isServerEcho = !msg.message_id.startsWith('temp_');
      let updated: typeof existing;
      const tempIdx = isServerEcho
        ? existing.findIndex(
            (m) =>
              m.message_id.startsWith('temp_') &&
              m.sender_id === msg.sender_id &&
              m.content === msg.content &&
              hasSameAttachmentSignature(m, msg),
          )
        : -1;

      const preview = buildMessagePreview(msg);

      if (tempIdx !== -1) {
        // temp 메시지를 실제 메시지로 교체 — 중복 없음
        updated = [...existing];
        updated[tempIdx] = msg;
      } else {
        updated = [...existing, msg];
      }

      // 대화 목록 메타 갱신
      const convs = s.conversations.map((c) => {
        if (c.conversation_id === convId) {
          return {
            ...c,
            last_message_at: msg.created_at,
            last_message_preview: preview,
            last_message_sender_id: msg.sender_id,
            // temp 교체/내가 보낸 메시지/현재 실제로 보고 있는 대화이면 unread 증가 안 함
            unread_count:
              tempIdx !== -1 || canAutoRead || !isIncoming
                ? c.unread_count
                : c.unread_count + 1,
          };
        }
        return c;
      });

      return {
        messages: {
          ...s.messages,
          [convId]: updated,
        },
        conversations: sortConversationsByRecent(convs),
      };
    });

    if (canAutoRead && isIncoming) {
      _pendingReadConvIds.delete(convId);
      apiMarkRead(convId).catch(() => { _pendingReadConvIds.add(convId); });
    } else if (isIncoming && isDMModeActive && isActive) {
      _pendingReadConvIds.add(convId);
    }
  },

  /* ---- 대화 메타 갱신 ---- */
  updateConversationMeta: (convId, patch) => {
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.conversation_id === convId ? { ...c, ...patch } : c
      ),
    }));
  },

  /* ---- 타이핑 상태 ---- */
  setTyping: (convId, userId, typing) => {
    set((s) => {
      const current = s.typingUsers[convId] || new Set<string>();
      const next = new Set(current);
      if (typing) next.add(userId);
      else next.delete(userId);
      return {
        typingUsers: { ...s.typingUsers, [convId]: next },
      };
    });
  },

  /* ---- 리액션 토글 ---- */
  toggleReaction: (convId, messageId, emoji, userId) => {
    set((s) => {
      const msgs = s.messages[convId];
      if (!msgs) return s;
      const idx = msgs.findIndex((m) => m.message_id === messageId);
      if (idx === -1) return s;

      const msg = msgs[idx];
      const reactions: Reaction[] = msg.reactions ? [...msg.reactions] : [];
      const rIdx = reactions.findIndex((r) => r.emoji === emoji);

      if (rIdx === -1) {
        reactions.push({ emoji, userIds: [userId] });
      } else {
        const existing = reactions[rIdx];
        if (existing.userIds.includes(userId)) {
          const newUserIds = existing.userIds.filter((id) => id !== userId);
          if (newUserIds.length === 0) {
            reactions.splice(rIdx, 1);
          } else {
            reactions[rIdx] = { ...existing, userIds: newUserIds };
          }
        } else {
          reactions[rIdx] = { ...existing, userIds: [...existing.userIds, userId] };
        }
      }

      const updated = [...msgs];
      updated[idx] = { ...msg, reactions };

      return {
        messages: {
          ...s.messages,
          [convId]: updated,
        },
      };
    });
    // 서버에 전파 → 멤버 전원에게 user_reaction 이벤트 broadcast(상대에게 보이게 함).
    // 서버 이벤트가 최종 권위이므로 실패는 조용히 무시(낙관적 상태는 유지).
    void apiToggleReaction(convId, messageId, emoji).catch(() => {});
  },

  setMessageReactions: (convId, messageId, reactions) => {
    set((s) => {
      const msgs = s.messages[convId];
      if (!msgs) return s;
      const idx = msgs.findIndex((m) => m.message_id === messageId);
      if (idx === -1) return s;
      const updated = [...msgs];
      updated[idx] = { ...msgs[idx], reactions };
      return { messages: { ...s.messages, [convId]: updated } };
    });
  },

  /* ---- 읽음 처리 ---- */
  markRead: async (convId) => {
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.conversation_id === convId ? { ...c, unread_count: 0 } : c
      ),
    }));
    _pendingReadConvIds.delete(convId);

    let lastErr: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await apiMarkRead(convId);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    console.error('[dmConversationStore] markRead failed after retries:', lastErr);
  },

  flushPendingReads: () => {
    if (_pendingReadConvIds.size === 0) return;
    const ids = [..._pendingReadConvIds];
    _pendingReadConvIds.clear();
    for (const convId of ids) {
      apiMarkRead(convId)
        .then(() => {
          useDMConversationStore.getState().updateConversationMeta(convId, { unread_count: 0 } as Partial<Conversation>);
        })
        .catch(() => { _pendingReadConvIds.add(convId); });
    }
  },

  leaveConversation: async (convId) => {
    try {
      await apiLeaveConversation(convId);
      set((s) => {
        const nextMessages = { ...s.messages };
        delete nextMessages[convId];
        const nextTyping = { ...s.typingUsers };
        delete nextTyping[convId];
        return {
          conversations: s.conversations.filter((c) => c.conversation_id !== convId),
          messages: nextMessages,
          typingUsers: nextTyping,
          activeConversation: s.activeConversation === convId ? null : s.activeConversation,
        };
      });
      return true;
    } catch (err) {
      console.error('[dmConversationStore] leaveConversation failed:', err);
      return false;
    }
  },

  syncCurrentUserProfile: ({ userId, name, email, picture }) => {
    if (!userId) return;
    set((s) => ({
      conversations: s.conversations.map((conv) => {
        if (!conv.members || conv.members.length === 0) return conv;
        let changed = false;
        const nextMembers = conv.members.map((member) => {
          if (member.user_id !== userId) return member;
          const updated = {
            ...member,
            ...(name !== undefined ? { name } : {}),
            ...(email !== undefined ? { email } : {}),
            ...(picture !== undefined ? { picture } : {}),
          };
          if (
            updated.name !== member.name
            || updated.email !== member.email
            || updated.picture !== member.picture
          ) {
            changed = true;
          }
          return updated;
        });
        return changed ? { ...conv, members: nextMembers } : conv;
      }),
    }));
  },

  /* ---- 초기화 ---- */
  clear: () => {
    set({
      conversations: [],
      activeConversation: null,
      messages: {},
      typingUsers: {},
      isLoaded: false,
    });
  },
}));
